import { Divider, Grid, useMediaQuery, useTheme } from "@material-ui/core";
import React, { useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom/cjs/react-router-dom";
import { scrollToTop } from "../../../../utils";
import "../style.css";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import {
  HttpCode,
  PAYMENT_TYPES,
  PROFILE_STATUS,
  SUPPORTED_CURRENCIES,
} from "../../../../config/constant";
import { createPayementSession } from "../../../../features/payment";
import { formatPaymentData, resetPaymentStates } from "./extension";
import PromoCodeSection from "./templates/promo-code-section";
import { useTranslation } from "react-i18next";
import Config from "../../../../config";
import PaymentSection from "./templates/payment-section";
import AmountRow from "./templates/discount-section";
import {
  setCheckoutError,
  setLoadingInvoiceData,
  setPaymentSession,
  setPromoCode,
  setPromoCodeError,
  setRenewalError,
} from "../../../../store/actions/pay-for-ad";

const { TWO_THREE_MONTH_PACKAGES_PROMOS } = Config;

const renewalProfileStatus = [PROFILE_STATUS.EXPIRED, PROFILE_STATUS.LIVE];

const CheckoutView = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation();

  const { authProfile } = useSelector((state) => state.application);
  const selectedCurrency = sessionStorage.getItem("checkout_currency");
  const recurringPayment =
    sessionStorage.getItem(PAYMENT_TYPES.IS_RECURRING) === "true";

  const { selectedPackageId, paymentSession } = useSelector(
    (state) => state.payForAd
  );
  const isMobileView = useMediaQuery("(max-width:700px)");

  const handlePaymentSession = useCallback(
    async ({ promo = null }) => {
      if (isEmpty(authProfile)) {
        return;
      }

      const postId = authProfile.id;
      const isRenewAccount = renewalProfileStatus.includes(authProfile.status);

      let data = {
        ...(promo && { promoCode: promo.trim() }),
        ...(recurringPayment && { isRecurring: recurringPayment }),
        isRenew: isRenewAccount,
        forceLKR: selectedCurrency !== SUPPORTED_CURRENCIES.USD,
        pricingTierId: selectedPackageId,
        simulate: true,
      };

      try {
        dispatch(setLoadingInvoiceData(true));
        const response = await createPayementSession({
          postId,
          data,
        });

        if (!response) {
          dispatch(setCheckoutError(true));
        }

        if (!response.success) {
          if (response.statusCode === HttpCode.BAD_REQUEST) {
            const errorCode = response.body?.code;
            switch (errorCode) {
              case 1400:
              case 1401:
              case 1402:
              case 1403:
              case 1311:
              case 1312:
                dispatch(setPromoCodeError(t(`serverError.${errorCode}`)));
                break;
              case 1404:
                if (TWO_THREE_MONTH_PACKAGES_PROMOS.includes(promo)) {
                  dispatch(
                    setPromoCodeError(
                      t("payment.promoCodeValidForTwoAndThreeMonthsPackages")
                    )
                  );
                } else {
                  dispatch(setPromoCodeError(t(`serverError.${errorCode}`)));
                }
                break;
              case 1504:
              case 1512:
                dispatch(setRenewalError(true));
                break;
              default:
                dispatch(setCheckoutError(true));
                break;
            }
          } else {
            dispatch(setCheckoutError(true));
          }
        }

        if (response.success) {
          const sessionData = formatPaymentData({
            sessionData: response.body,
            packageId: selectedPackageId,
          });
          dispatch(setPaymentSession(sessionData));
          localStorage.setItem(
            "session_creation",
            JSON.stringify(response.body)
          );
          if (response.body.promoCode) {
            localStorage.setItem("promoCode", response.body.promoCode);
            dispatch(setPromoCode(response.body.promoCode));
          } else {
            localStorage.removeItem("promoCode");
            dispatch(setPromoCode(undefined));
          }
        }
      } catch (error) {
        dispatch(setCheckoutError(true));
      } finally {
        dispatch(setLoadingInvoiceData(false));
      }
    },
    [
      authProfile,
      selectedCurrency,
      t,
      dispatch,
      selectedPackageId,
      recurringPayment,
    ]
  );

  useEffect(() => {
    return () => {
      dispatch(resetPaymentStates());
    };
  }, [dispatch, location]);

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    handlePaymentSession({});
  }, [handlePaymentSession]);

  return (
    <Grid container direction="row" className="checkout-view">
      <Grid
        container
        direction="column"
        style={{
          rowGap: 12,
          width: isMobileView ? "100%" : "48%",
          padding: isMobileView ? 0 : "0 16px",
        }}
      >
        {!recurringPayment && (
          <PromoCodeSection handlePaymentSession={handlePaymentSession} />
        )}

        <AmountRow
          label={t("payment.packageAmount")}
          amount={paymentSession.amountBeforeDiscount}
          visibility={true}
        />

        <AmountRow
          label={t(
            recurringPayment
              ? "payment.subscriptionDiscount"
              : "payment.renewalDiscount",
            {
              discount: paymentSession.discountPercentage,
            }
          )}
          amount={paymentSession.discountAmount}
          visibility={
            paymentSession.discountPercentage && !paymentSession.promoCode
          }
          isDiscount={true}
        />

        <AmountRow
          label={t("payment.promoDiscount", {
            discount: paymentSession.discountPercentage,
          })}
          amount={paymentSession.discountAmount}
          visibility={
            paymentSession.discountPercentage && paymentSession.promoCode
          }
          isDiscount={true}
        />

        {!isMobileView && <Divider style={{ width: "100%" }} />}

        <AmountRow
          label={t("payment.totalDue")}
          amount={paymentSession.amount}
          visibility={true}
          isHighlightedText={true}
        />
      </Grid>
      {isMobileView && <Divider style={{ width: "100%" }} />}
      {!isMobileView && (
        <Grid container className="vertical-divider">
          <Divider orientation="vertical" flexItem />
        </Grid>
      )}
      <PaymentSection />
    </Grid>
  );
};

export default CheckoutView;
